import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import api from '../services/api';

const ProjectWorkspaceDashboard = () => {
  const { projectId, project, canManage } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState('tout');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustomDates, setShowCustomDates] = useState(false);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, periodType, customStart, customEnd]);

  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (periodType) {
      case 'tout':
        start = '2000-01-01';
        end = now.toISOString().split('T')[0];
        break;
      case 'aujourdhui':
        start = now.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'semaine':
        const weekStart = new Date(now);
        const day = weekStart.getDay();
        const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1); // Lundi
        weekStart.setDate(diff);
        start = weekStart.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'mois':
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'annee':
        start = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'custom':
        start = customStart || new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        end = customEnd || now.toISOString().split('T')[0];
        break;
      default:
        start = '2000-01-01';
        end = now.toISOString().split('T')[0];
    }

    return { start, end };
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriodType(newPeriod);
    if (newPeriod === 'custom') {
      setShowCustomDates(true);
    } else {
      setShowCustomDates(false);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      const res = await api.get(`/projects/${projectId}/workspace/dashboard`, {
        params: { date_debut: start, date_fin: end }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">
            Tableau de bord
          </h1>
          <p className="text-gray-500 text-sm">
            Finances du projet: {project?.title}
          </p>
        </div>

        {/* Bouton Parts Collaborateurs + Sélecteur de période */}
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/projects/${projectId}/finances/collaborators`}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl hover:from-violet-600 hover:to-purple-700 transition shadow-lg hover:shadow-xl font-medium"
          >
            <span>👥</span>
            <span>Parts Collaborateurs</span>
          </Link>
          
          {/* Boutons de période */}
          <div className="flex flex-wrap gap-1">
            {[
              { key: 'tout', label: 'Tout' },
              { key: 'aujourdhui', label: "Aujourd'hui" },
              { key: 'semaine', label: 'Semaine' },
              { key: 'mois', label: 'Mois' },
              { key: 'annee', label: 'Année' },
              { key: 'custom', label: '📅' },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => handlePeriodChange(p.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  periodType === p.key 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sélecteur de dates personnalisées */}
      {showCustomDates && (
        <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Du:</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Au:</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          {customStart && customEnd && (
            <span className="text-sm text-purple-600 font-medium">
              📊 Période: {new Date(customStart).toLocaleDateString('fr-FR')} - {new Date(customEnd).toLocaleDateString('fr-FR')}
            </span>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        {/* Chiffre d'affaires */}
        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl md:text-2xl">📊</span>
            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">CA</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-gray-800">
            {formatMoney(stats?.chiffre_affaires)} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Chiffre d'affaires</div>
        </div>

        {/* Coût des ventes */}
        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl md:text-2xl">🏷️</span>
            <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">Coût</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-gray-800">
            {formatMoney(stats?.cout_ventes)} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Coût des ventes</div>
          {/* Info achats stock */}
          {(stats?.achats_stock > 0 || stats?.cout_ventes > 0) && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Achats stock:</span>
                <span className="text-orange-600 font-medium">-{formatMoney(stats?.achats_stock || 0)} XOF</span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-gray-500">Dispo. ravitaillement:</span>
                <span className={`font-medium ${((stats?.cout_ventes || 0) - (stats?.achats_stock || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatMoney((stats?.cout_ventes || 0) - (stats?.achats_stock || 0))} XOF
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Marge brute */}
        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl md:text-2xl">💰</span>
            <span className={`text-xs px-2 py-1 rounded-full ${stats?.marge_brute >= 0 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'}`}>
              Marge
            </span>
          </div>
          <div className={`text-lg md:text-xl font-bold ${stats?.marge_brute >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatMoney(stats?.marge_brute)} <span className="text-xs md:text-sm font-normal">XOF</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Marge brute</div>
        </div>

        {/* Dépenses */}
        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl md:text-2xl">💸</span>
            <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">Dépenses</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-gray-800">
            {formatMoney(stats?.depenses)} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Dépenses</div>
        </div>

        {/* Bénéfice */}
        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl md:text-2xl">📈</span>
            <span className={`text-xs px-2 py-1 rounded-full ${stats?.benefice >= 0 ? 'text-emerald-600 bg-emerald-100' : 'text-red-600 bg-red-100'}`}>
              Bénéfice
            </span>
          </div>
          <div className={`text-lg md:text-xl font-bold ${stats?.benefice >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {stats?.benefice >= 0 ? '+' : ''}{formatMoney(stats?.benefice)} <span className="text-xs md:text-sm font-normal">XOF</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Bénéfice net</div>
        </div>
      </div>

      {/* Trésorerie row (Caisse) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow p-4 md:p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl md:text-2xl">🏦</span>
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Caisse</span>
          </div>
          <div className="text-xl md:text-2xl font-bold">{formatMoney(stats?.tresorerie)} <span className="text-xs md:text-sm font-normal opacity-80">XOF</span></div>
          <div className="text-xs opacity-80 mt-1">Trésorerie disponible</div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl md:text-2xl">⬇️</span>
            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">Entrées</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-green-600">+{formatMoney(stats?.encaissements)} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span></div>
          <div className="text-xs text-gray-500 mt-1">Encaissements</div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xl md:text-2xl">⬆️</span>
            <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">Sorties</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-red-600">-{formatMoney(stats?.decaissements)} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span></div>
          <div className="text-xs text-gray-500 mt-1">Décaissements</div>
        </div>
      </div>

      {/* Stats secondaires */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Factures impayées */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-amber-500 text-xl mb-2">📋</div>
          <div className="text-lg font-bold text-amber-600">
            {formatMoney(stats?.factures_impayees)} <span className="text-xs font-normal">XOF</span>
          </div>
          <div className="text-xs text-gray-500">Factures impayées</div>
        </div>

        {/* Prestations impayées */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-orange-500 text-xl mb-2">🔧</div>
          <div className="text-lg font-bold text-orange-600">
            {formatMoney(stats?.prestations_impayees)} <span className="text-xs font-normal">XOF</span>
          </div>
          <div className="text-xs text-gray-500">Prestations impayées</div>
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-indigo-500 text-xl mb-2">🔄</div>
          <div className="text-lg font-bold text-indigo-600">
            {stats?.nb_transactions || 0}
          </div>
          <div className="text-xs text-gray-500">Transactions</div>
        </div>

        {/* Prestations en cours */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-cyan-500 text-xl mb-2">⏳</div>
          <div className="text-lg font-bold text-cyan-600">
            {stats?.prestations_en_cours || 0}
          </div>
          <div className="text-xs text-gray-500">Prestations en cours</div>
        </div>
      </div>

      {/* Accès rapides */}
      <div className="bg-white rounded-xl shadow p-4">
        <h3 className="font-semibold text-gray-800 mb-4">Accès rapides</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Link
            to={`/projects/${projectId}/workspace/payments`}
            className="flex items-center gap-3 p-3 rounded-xl bg-green-50 hover:bg-green-100 transition"
          >
            <span className="text-2xl">💰</span>
            <span className="text-sm font-medium text-gray-700">Encaissements</span>
          </Link>
          <Link
            to={`/projects/${projectId}/workspace/expenses`}
            className="flex items-center gap-3 p-3 rounded-xl bg-red-50 hover:bg-red-100 transition"
          >
            <span className="text-2xl">📤</span>
            <span className="text-sm font-medium text-gray-700">Dépenses</span>
          </Link>
          <Link
            to={`/projects/${projectId}/workspace/invoices`}
            className="flex items-center gap-3 p-3 rounded-xl bg-blue-50 hover:bg-blue-100 transition"
          >
            <span className="text-2xl">🧾</span>
            <span className="text-sm font-medium text-gray-700">Factures</span>
          </Link>
          <Link
            to={`/projects/${projectId}/workspace/report`}
            className="flex items-center gap-3 p-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition"
          >
            <span className="text-2xl">📈</span>
            <span className="text-sm font-medium text-gray-700">Bilan</span>
          </Link>
          <Link
            to={`/projects/${projectId}/finances/collaborators`}
            className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-violet-100 to-purple-100 hover:from-violet-200 hover:to-purple-200 transition border-2 border-violet-300"
          >
            <span className="text-2xl">👥</span>
            <span className="text-sm font-medium text-violet-700">Parts Collaborateurs</span>
          </Link>
        </div>
      </div>

      {/* Info permission */}
      {!canManage && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">👁️</span>
            <div>
              <h4 className="font-medium text-amber-800">Mode consultation</h4>
              <p className="text-sm text-amber-700">
                Vous pouvez consulter les finances du projet, mais vous ne pouvez pas ajouter ou modifier de données.
                Contactez le chef de projet pour obtenir les droits de gestion.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectWorkspaceDashboard;
